import type { components } from "@/api/schema";
import type { DashboardData, RouteTrack, StepView } from "@/app/queries";
import { count } from "@/lib/plural";

export type Deadline = components["schemas"]["Deadline"];

/* Чистые вычисления дашборда S1/S8: всё — из данных, ничего строкой. */

export type NextAction = { kind: "unit"; n: number; topic: string } | { kind: "practice" };

/** Куда вести «продолжить»: первый неотмеченный юнит первого ресурса с гайдом
 *  (номер — внутри ресурса, как в S4), если все юниты отмечены — практика. */
export function nextAction(cur: StepView): NextAction {
  const ticked = new Set(cur.ticks.units);
  for (const r of cur.content.resources) {
    if (r.units.length === 0) continue;
    const i = r.units.findIndex((u) => !ticked.has(u.key));
    if (i >= 0) return { kind: "unit", n: i + 1, topic: r.units[i]!.topic };
  }
  return { kind: "practice" };
}

export function continueLabel(a: NextAction, short = false): string {
  if (a.kind === "practice") return "продолжить: практика";
  return short ? `продолжить: юнит ${a.n}` : `продолжить: юнит ${a.n} · ${a.topic}`;
}

export type Bar = { label: string; done: number; total: number };

/** Три полосы «сейчас»: ресурсы закрыты целиком (все юниты, либо отметка
 *  ресурса без гайда), практика базовая и применение — от отметок. */
export function stepBars(cur: StepView): Bar[] {
  const units = new Set(cur.ticks.units);
  const tasks = new Set(cur.ticks.tasks);
  const resources = new Set(cur.ticks.resources);
  const { content } = cur;
  // Только ресурсы гейта: «Первоисточник» (optional) и ⏭️-юниты не считаются.
  const gate = content.resources.filter((r) => !r.optional);
  const resDone = gate.filter((r) => {
    const req = r.units.filter((u) => !u.skip);
    return req.length > 0 ? req.every((u) => units.has(u.key)) : resources.has(r.id);
  }).length;
  const doneOf = (list: { id: string }[]) => list.filter((t) => tasks.has(t.id)).length;
  return [
    { label: "ресурсы", done: resDone, total: gate.length },
    { label: "практика · базовый", done: doneOf(content.practice.base), total: content.practice.base.length },
    { label: "практика · применение", done: doneOf(content.practice.apply), total: content.practice.apply.length },
  ];
}

/** Подпись срока: «через 7 дней», «сегодня», «просрочено на 1 день». */
export function deadlineLeft(daysLeft: number): string {
  if (daysLeft === 0) return "сегодня";
  if (daysLeft < 0) return `просрочено на ${count(-daysLeft, "день", "дня", "дней")}`;
  return `через ${count(daysLeft, "день", "дня", "дней")}`;
}

/** Тон срока: просрочен — err, неделя и ближе — warn, дальше — обычный. */
export function deadlineTone(daysLeft: number): "err" | "warn" | "ink2" | "mut" {
  if (daysLeft < 0) return "err";
  if (daysLeft <= 7) return "warn";
  if (daysLeft <= 14) return "ink2";
  return "mut";
}

/** «2026-09-21» → «21.09». */
export function shortDate(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split("-");
  return m && d ? `${d}.${m}` : iso;
}

export function sortDeadlines(list: Deadline[]): Deadline[] {
  return [...list].sort((a, b) => a.days_left - b.days_left);
}

export function mainTracks(tracks: RouteTrack[]): RouteTrack[] {
  return tracks.filter((t) => !t.parallel);
}

export function parallelTrack(tracks: RouteTrack[]): RouteTrack | undefined {
  return tracks.find((t) => t.parallel);
}

/** Текст шапки: «когорта ML-осень-2026 · ментор Никита» без пустых частей. */
export function headerMeta(d: Pick<DashboardData, "cohort" | "mentor">): string | undefined {
  const parts: string[] = [];
  if (d.cohort) parts.push(`когорта ${d.cohort}`);
  if (d.mentor) parts.push(`ментор ${d.mentor}`);
  return parts.length ? parts.join(" · ") : undefined;
}

const PROCEDURE = "Метрики не важны — важна корректность процедуры.";

/** Лид «сейчас»: что откроет гейт — следующий шаг из маршрута, иначе
 *  текст зависимости из документа (в сиде он с глаголом: «открывает шаг 2 · …»). */
export function gateLead(cur: StepView): string {
  const fromDeps = cur.content.deps.opens.trim().replace(/^открывает\s+/i, "");
  const opens = cur.next ? `шаг ${cur.next.position} «${cur.next.title}»` : fromDeps;
  if (!opens) return `Это последний шаг трека. ${PROCEDURE}`;
  return `Гейт «Готов к переходу» откроет ${opens}. ${PROCEDURE}`;
}
